import React from "react";
import { useDispatch } from "react-redux";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  GetPublicUserProfileResponse,
  MyProfileResponse,
  UserPreferenceEnum,
} from "@dripit/types";

import { queryKeys } from "@/api/constants";
import { client } from "@/api/base";
import { profileActions, RootState } from "@/store";
import {
  PagedDataResponse,
  WalletActivity,
  ClaimTicketResponse,
} from "@/types";
import isNil from "lodash.isnil";
import { useAuth } from "@/hooks";
import { isProduction } from "@/helpers";

interface UseProfileProps {
  enabled?: boolean;
}

export const useProfile = ({ enabled = true }: UseProfileProps = {}) => {
  const dispatch = useDispatch();
  const { isAuthenticated, isInitialized } = useAuth();
  const query = useQuery<MyProfileResponse>({
    queryKey: [queryKeys.PROFILE],
    queryFn: () => client.clearParams().get<MyProfileResponse>("users/profile"),
    enabled: isAuthenticated && isInitialized && enabled,
  });
  const { data, isLoading, error } = query;

  React.useEffect(() => {
    dispatch(profileActions.setIsLoading(isLoading));
  }, [isLoading]);

  React.useEffect(() => {
    if (error) {
      dispatch(profileActions.setError(JSON.stringify(error)));
    }
  }, [error]);

  React.useEffect(() => {
    if (data && isAuthenticated && isInitialized) {
      dispatch(profileActions.setProfile(data));
    }
  }, [data, isAuthenticated, isInitialized]);

  return query;
};

export const usePublicUserProfile = (walletAddress?: string) =>
  useQuery<GetPublicUserProfileResponse>({
    queryKey: [queryKeys.PUBLIC_PROFILE, walletAddress],
    queryFn: () =>
      client
        .clearParams()
        .get<GetPublicUserProfileResponse>(`users/profile/${walletAddress}`),
    staleTime: 10_000,
    enabled: !isNil(walletAddress),
  });

export const useWalletActivities = (page: number = 1) =>
  useQuery<PagedDataResponse<WalletActivity>>({
    queryKey: [queryKeys.WALLET_ACTIVITIES, page],
    queryFn: () =>
      client
        .setConfig({ params: { page, pageSize: 25 } })
        .get<PagedDataResponse<WalletActivity>>("wallet/activities"),
  });

interface ClaimTicketVariables {
  qr: string;
}

export const useClaimTicket = (claim: boolean) =>
  useMutation({
    mutationFn: ({ qr }: ClaimTicketVariables) =>
      client
        .clearParams()
        .post<ClaimTicketResponse>(`tickets/claim`, { qr, claim }),
  });

interface UpdateAvatarVariables {
  file: File;
}

export const useUpdateAvatar = () =>
  useMutation({
    mutationFn: ({ file }: UpdateAvatarVariables) => {
      const formData = new FormData();
      formData.append("avatar", file);

      return client
        .clearParams()
        .setConfig({
          headers: {
            "Content-Type": "multipart/form-data",
          },
        })
        .post<string>("upload/avatar", formData);
    },
  });

interface UpdateUserPreferenceVariables {
  key: UserPreferenceEnum;
  value: string | number | boolean;
}

export const useUpdateUserPreference = () =>
  useMutation({
    mutationFn: ({ key, value }: UpdateUserPreferenceVariables) =>
      client.clearParams().post<string>("users/set-preference", { key, value }),
  });

interface UseSwapedWidgetArgs {
  currencyCode?: string;
}

export const useSwapedWidget = ({ currencyCode }: UseSwapedWidgetArgs = {}) =>
  useQuery<string>({
    queryKey: [queryKeys.SWAPED_WIDGET],
    queryFn: async () => {
      const currency = !isProduction ? "ETH" : (currencyCode ?? "USDC_AVAX");
      const url = await client.get<string>(
        `swapped-service/sign-url?currencyCode=${currency}`
      );

      if (!isProduction) {
        return url.replace(
          "https://widget.swapped.com",
          "https://sandbox.swapped.com"
        );
      }

      return url;
    },
  });
